import React, { useEffect, useState } from "react";
import { useResponsibleQuery } from "../../../user/hooks/useResponsibleQuery.ts";
import { controlModel, ControlRowViewModel, ControlRowViewModelTableModel } from "./ControlsForm.types.ts";
import { ControlsFormProps } from "./ControlsForm.tsx";
import { useFilterList } from "../../../common/hooks/useFilterList.ts";
import { SelectableItem } from "../../../processingActivity/ProcessingActivity.types.ts";
import { addMonths, format } from "date-fns";
import { isNullOrWhitespace } from "../../../common/stringOperations.ts";
import { useGroupEntitiesQuery } from "../../../processingActivity/hooks/useGroupEntitiesQuery.ts";
import { useTranslation } from "../../../localization/useTranslation.ts";

import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";

export function useControlsForm(props: ControlsFormProps) {
    const selectedControls = props.selectedControls ?? [];
    const [searchedResponsible, setSearchResponsible] = useState<Array<string>>([]);
    const [searchedCategory, setSearchedCategory] = useState<Array<string>>([]);
    const [searchedDeadline, setSearchedDeadline] = useState<Array<string>>([]);
    const [searchedImplementationGroups, setSearchedImplementationGroups] = useState<Array<string>>([]);

    const url = `/frameworks/templates/${props.frameworkId}/control/selectable`;
    const { isLoading, data } = useDotLegalQuery<Array<ControlRowViewModel>>(url);
    const responsibleQuery = useResponsibleQuery();
    const selectableResponsibles = responsibleQuery.data("") ?? [];

    useEffect(() => {
        if (data && props.selectedControls === undefined) {
            let selectedDrafts = data.map((x) => {
                const existingControl = selectedControls.find((c) => c.controlId === x.id);
                return new controlModel(x.id, true, [], addMonths(new Date(), 1), existingControl?.responsible ?? props.overallResponsible, undefined);
            });

            props.onChange(selectedDrafts);
        }
        // eslint-disable-next-line
    }, [data]);

    const formattedTableData = formatDataToTable(data ?? []);
    let categoryFilterOptions: Array<SelectableItem> = [];
    let responsibleFilterOptions: Array<SelectableItem> = [];
    let deadlineFilterOptions: Array<SelectableItem> = [];
    let implementationGroupsFilterOptions: Array<SelectableItem> = [];
    if (data && responsibleQuery) {
        createCategoryFilterOptions(data.flatMap((x) => x.category));
        createResponsibleAndDeadlineFilterOptions();
        createImplementationGroupFilterOptions(data);
    }

    let filteredTableData: ControlRowViewModelTableModel[] = useFilterList(formattedTableData ?? [], [
        { key: "category", searchValues: searchedCategory, objectId: "id" },
        { key: "responsible", searchValues: searchedResponsible, objectId: "id" },
        { key: "deadlineString", searchValues: searchedDeadline, objectId: "id" },
        { key: "implementationGroup", searchValues: searchedImplementationGroups, objectId: "id" },
    ]);

    const modelUpdater = {
        onSelectAllChange: (selected: Array<ControlRowViewModelTableModel>) => {
            if (selected.length === 0) {
                const newModel = selectedControls.map((control) => {
                    return control.withUpdates({ isSelected: false });
                });
                props.onChange(newModel);
            } else {
                const newModel = selectedControls.map((control) => {
                    if (!control.isSelected) {
                        return control.withUpdates({ isSelected: true });
                    }

                    return control;
                });
                props.onChange(newModel);
            }
        },
        onControlChange: (row: ControlRowViewModelTableModel) => {
            const newModel = selectedControls.map((control) => {
                if (control.controlId === row.id) {
                    if (control.isSelected) {
                        return control.withUpdates({ isSelected: false, groupEntityIds: [] });
                    } else {
                        return control.withUpdates({ isSelected: true });
                    }
                }

                return control;
            });

            props.onChange(newModel);
        },
        onResponsibleChange: (controlId: string, responsible: string) => {
            const newModel = selectedControls.map((control) => {
                if (control.controlId === controlId) {
                    return control.withUpdates({ responsible });
                }

                return control;
            });

            props.onChange(newModel);
        },
        onDeadlineChange: (controlId: string, date: Date | null) => {
            const newModel = selectedControls.map((control) => {
                if (control.controlId === controlId) {
                    return control.withUpdates({ deadline: date ?? new Date() });
                }

                return control;
            });

            props.onChange(newModel);
        },
        onNoteChange: (controlId: string, note: string) => {
            const newModel = selectedControls.map((control) => {
                if (control.controlId === controlId) {
                    return control.withUpdates({ note: note });
                }

                return control;
            });

            props.onChange(newModel);
        },
        onGroupEntityChange: (controlId: string, groupEntityIds: Array<string>) => {
            const control = selectedControls.find((x) => x.controlId === controlId);
            if (control) {
                if (
                    control.groupEntityIds.length !== 0 &&
                    !control.groupEntityIds.some((x) => x.includes("null")) &&
                    groupEntityIds.some((x) => x.includes("null"))
                ) {
                    groupEntityIds = [];
                } else if (groupEntityIds.length > 1) {
                    groupEntityIds = groupEntityIds.filter((x) => x !== "null");
                }

                const newModel = selectedControls.map((control) => {
                    if (control.controlId === controlId) {
                        return control.withUpdates({ groupEntityIds });
                    }

                    return control;
                });

                props.onChange(newModel);
            }
        },
    };

    const getHeaderCheckBoxState = () => {
        const allSelected = selectedControls.every((x) => x.isSelected);
        const noneSelected = selectedControls.every((x) => !x.isSelected);

        return noneSelected ? "false" : allSelected ? "true" : "indeterminate";
    };

    return {
        modelUpdater,
        isLoading,
        data: filteredTableData,
        responsibleIsLoading: responsibleQuery.isLoading,
        selectableResponsibles: selectableResponsibles,
        getHeaderCheckBoxState,
        searchedResponsible,
        setSearchResponsible,
        searchedCategory,
        setSearchedCategory,
        searchedDeadline,
        setSearchedDeadline,
        categoryFilterOptions,
        responsibleFilterOptions,
        deadlineFilterOptions,
        implementationGroupsFilterOptions,
        searchedImplementationGroups,
        setSearchedImplementationGroups,
    };

    function formatDataToTable(data: Array<ControlRowViewModel>): Array<ControlRowViewModelTableModel> {
        return data.map((control) => {
            const controlFromSaveModel = selectedControls.find((x) => x.controlId === control.id);
            const isSelected = controlFromSaveModel?.isSelected ?? false;
            const deadline = controlFromSaveModel?.deadline ?? null;
            const responsible = !isSelected ? undefined : (controlFromSaveModel?.responsible ?? props.overallResponsible);
            return {
                ...control,
                isSelected: isSelected,
                deadline: deadline,
                deadlineString: deadline === null ? "" : format(deadline, "LLLL yyyy"),
                responsible: responsible,
                responsibleString: responsible === undefined ? "" : (selectableResponsibles.find((x) => x.id === responsible)?.name ?? ""),
                categoryString: control.category.name,
                noteRequired: !isSelected && isNullOrWhitespace(controlFromSaveModel?.note),
                groupEntityIds: controlFromSaveModel?.groupEntityIds ?? [],
            };
        });
    }

    function createCategoryFilterOptions(categories: Array<SelectableItem>) {
        categories.forEach((x) => {
            if (!categoryFilterOptions.some((option) => option.id === x.id)) {
                categoryFilterOptions.push({ id: x.id, name: x.name });
            }
        });
    }

    function createImplementationGroupFilterOptions(rows: Array<ControlRowViewModel>) {
        rows.filter((x) => !isNullOrWhitespace(x.implementationGroup)).forEach((x) => {
            if (!implementationGroupsFilterOptions.some((option) => option.id === x.implementationGroup)) {
                implementationGroupsFilterOptions.push({ id: x.implementationGroup!, name: x.implementationGroup! });
            }
        });

        implementationGroupsFilterOptions = implementationGroupsFilterOptions.sort((a, b) => a.name.localeCompare(b.name));
    }

    function createResponsibleAndDeadlineFilterOptions() {
        selectedControls.forEach((x) => {
            if (!responsibleFilterOptions.some((option) => option.id === x.responsible)) {
                let user = selectableResponsibles.find((y) => y.id === x.responsible);
                if (user) {
                    responsibleFilterOptions.push({ id: x.responsible!, name: user.name });
                }
            }

            if (x.deadline && !deadlineFilterOptions.some((option) => option.id === format(x.deadline!, "LLLL yyyy"))) {
                const formatedDate = format(x.deadline!, "LLLL yyyy");
                deadlineFilterOptions.push({ id: formatedDate, name: formatedDate });
            }
        });
    }
}

export function useGetSelectableGroupEntities(onlyForGroupEntities: Array<string>) {
    const { allGroupEntities, isLoading, showGroupEntitySelector } = useGroupEntitiesQuery();
    const { translateString } = useTranslation();

    const groupEntities = React.useMemo(() => {
        if (!allGroupEntities) {
            return [];
        }

        const allOption = {
            id: "null",
            name: translateString("allGroupEntities"),
            address: "",
            city: "",
            country: "",
            isActive: true,
            zipCode: "",
            users: undefined,
        };

        if (onlyForGroupEntities.length === 0) {
            return [allOption, ...allGroupEntities];
        }

        const filtered = allGroupEntities.filter((entity) => onlyForGroupEntities.includes(entity.id));
        return [allOption, ...filtered];
    }, [allGroupEntities, onlyForGroupEntities, translateString]);

    return { isLoading, groupEntities, showGroupEntitySelector };
}
